import React, { useState, useEffect } from "react";
import TaskItem from "../Tasks/TaskItem";
import LoadingComponent from "../../components/common/LoadingComponent";
import { ALL_CONVERSATION_INTEGRATIONS } from "./Conversation.utils";
import { useConversationContext } from "./ConversationContext";

function TasksColumn({ tasksData, members = [] }) {
  const { meta } = useConversationContext();
  const {
    tasks,
    allTasks,
    allTasksCount,
    hasSelection,
    taskLoading,
    onToggleCompleted,
    onRemove,
    replyProps,
    platformProps,
  } = tasksData;

  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!hasSelection) setShowAll(false);
  }, [hasSelection]);

  const groupPlatform = String(meta.group?.platform || "").toLowerCase();
  const isMessengerOrInstagram =
    ALL_CONVERSATION_INTEGRATIONS.includes(groupPlatform);

  const forceShowAll = isMessengerOrInstagram && hasSelection;
  const canReplyToTask = groupPlatform !== "intercom";

  const displayedTasks =
    hasSelection && !showAll && !forceShowAll ? tasks : allTasks;

  return (
    <div className="d-flex flex-column conv-tasks-column">
      <div className="px-3 pt-3 pb-2 flex-shrink-0 border-bottom border-secondary-subtle">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <h6 className="fw-semibold mb-1 d-flex align-items-center gap-2 text-body">
            <i className="bi bi-check2-square" />
            Tasks
            <span className="badge bg-secondary-subtle text-secondary-emphasis rounded-pill fw-normal conv-tasks-count-badge">
              {displayedTasks.length}
            </span>
          </h6>

          {hasSelection && !forceShowAll && (
            <button
              type="button"
              className="btn btn-sm btn-link p-0 text-decoration-none flex-shrink-0 conv-tasks-toggle-btn"
              onClick={() => setShowAll((s) => !s)}
            >
              {showAll ? "Show this message" : "Show all tasks"}
            </button>
          )}
        </div>
        <small className="text-body-secondary">
          {hasSelection && !showAll && !forceShowAll
            ? "For the selected message"
            : `All ${allTasksCount} tasks in this conversation`}
        </small>
      </div>

      <div className="flex-grow-1 px-3 py-3 conv-tasks-scroll">
        {taskLoading && displayedTasks.length === 0 ? (
          <LoadingComponent loading={true} size={10} />
        ) : displayedTasks.length === 0 ? (
          <div className="text-center text-body-secondary py-5">
            <i className="bi bi-check2-square fs-2 d-block mb-2" />
            {hasSelection && !showAll && !forceShowAll
              ? "No tasks for this message yet"
              : "No tasks yet — click the + next to a message to add one"}
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {displayedTasks.map((taskItem) => {
              const taskHandlers = { onToggleCompleted, onRemove };

              const itemReplyProps = {
                replyOpen: replyProps.replyOpenId === taskItem.uuid,
                replyDraft: replyProps.replyDrafts[taskItem.uuid],
                onToggleReply: replyProps.onToggleReply,
                onReplyChange: replyProps.onReplyChange,
                onSendReply: replyProps.onSendReply,
                isSendingReply: replyProps.sendingReplyIds?.has(taskItem.uuid),
              };

              const itemPlatformProps = {
                platforms: platformProps.platforms,
                platformMenuOpen:
                  platformProps.platformMenuOpenId === taskItem.uuid,
                onTogglePlatformMenu: () =>
                  platformProps.onTogglePlatformMenu(taskItem.uuid),
                onSelectPlatform: (platform) =>
                  platformProps.onSelectPlatform(taskItem, platform),
              };

              return (
                <TaskItem
                  key={taskItem.uuid}
                  taskItem={taskItem}
                  taskHandlers={taskHandlers}
                  replyProps={itemReplyProps}
                  platformProps={itemPlatformProps}
                  members={members}
                  onAssignMembers={tasksData.onAssignMembers}
                  canReply={canReplyToTask}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default TasksColumn;